import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { CALLS_TRIAGE_DEMO_UI_ENABLED } from '@/constants'
import type { CallRecordListItem } from '@/hooks/use-call-records'
import {
  CallInitialsLandmark,
  callerPrimaryLabel,
  callRowSubtitle,
} from '@/components/calls/call-identity-landmark'
import {
  formatDurationClock,
  formatElapsedSinceIso,
  formatRelativeTimeIso,
  formatStartedAtLocalClock,
} from '@/lib/time-format'

function badgeVariantForStatus(status: string) {
  if (status === 'ended' || status === 'completed') return 'secondary'
  if (status === 'in-progress' || status === 'ringing') return 'default'
  if (status === 'evaluation_failed') return 'destructive'
  return 'outline'
}

function isStubEvaluationSummary(text: string | undefined) {
  return (text ?? '').trimStart().startsWith('Stub evaluation:')
}

export type CompletionDwellProps = {
  secondsLeft: number
  /** 0–100, drains as dwell elapses */
  progress: number
}

export function LiveCallCard({
  row,
  completionDwell,
}: {
  row: CallRecordListItem
  completionDwell?: CompletionDwellProps | null
}) {
  const [, setTick] = useState(0)
  const inCompletionDwell = completionDwell != null && completionDwell.secondsLeft >= 0
  const live = !row.is_complete || inCompletionDwell
  const summaryPreview = row.summary_preview?.trim() ?? ''
  const evalSummary = row.evaluation_summary ?? ''
  const stubEval = isStubEvaluationSummary(evalSummary)
  const triageSummary = row.triage_summary?.trim() ?? ''
  const callState = (row.call_state || 'routine').toLowerCase()
  const subtitle = callRowSubtitle(row)
  const livePreview = row.live_transcript_preview?.trim() ?? ''
  const previewKind = row.live_preview_kind

  let primaryBlock: { label: string; text: string } | null = null
  let triageSecondary: { label: string; text: string } | null = null

  if (live && livePreview) {
    let label = row.live_transcript_role || 'Live'
    if (previewKind === 'speech_status') label = 'Status'
    else if (previewKind === 'conversation') label = 'Conversation'
    primaryBlock = { label, text: livePreview }
    if (triageSummary) {
      triageSecondary = { label: 'Triage', text: triageSummary }
    }
  } else if (live && triageSummary) {
    primaryBlock = { label: 'Triage', text: triageSummary }
  } else if (!live) {
    if (summaryPreview) {
      primaryBlock = { label: 'Summary', text: summaryPreview }
    } else if (triageSummary) {
      primaryBlock = { label: 'Triage', text: triageSummary }
    }
  }

  const started = row.started_at

  useEffect(() => {
    if (!live || !started) return
    const id = window.setInterval(() => setTick((t) => t + 1), 1000)
    return () => window.clearInterval(id)
  }, [live, started])

  const nowMs = Date.now()
  const elapsed = formatElapsedSinceIso(started, nowMs)
  const startedAtClock = started ? formatStartedAtLocalClock(started, nowMs) : null
  const createdFallback = !started ? formatRelativeTimeIso(row.created_at, nowMs) : null

  const borderForLive =
    callState === 'emergency'
      ? 'border-destructive ring-2 ring-destructive/35 shadow-sm'
      : callState === 'elevated'
        ? 'border-warning ring-1 ring-warning/30'
        : 'border-primary ring-1 ring-primary/25'

  const borderForDwell =
    'border-warning/80 ring-1 ring-warning/25 shadow-sm'

  const callerLabel = callerPrimaryLabel(row)

  return (
    <div
      className={cn(
        'relative rounded-lg border bg-card text-card-foreground shadow-sm transition-colors hover:bg-muted/40',
        inCompletionDwell && borderForDwell,
        live && !inCompletionDwell && borderForLive,
        !live && !inCompletionDwell && 'border-border'
      )}
    >
      <Link
        to={`/calls/records/${row.id}`}
        className="absolute inset-0 z-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`View call for ${callerLabel}, ${row.vapi_call_id}`}
      />
      <div className="relative z-10 p-4 space-y-3 pointer-events-none">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5 min-w-0">
            {live ? (
              <Badge
                variant={inCompletionDwell ? 'warningSoft' : 'default'}
                className="font-normal shrink-0"
              >
                {inCompletionDwell ? 'Ending' : 'Live'}
              </Badge>
            ) : null}
            {callState === 'emergency' ? (
              <Badge variant="destructive" className="font-normal shrink-0">
                Emergency
              </Badge>
            ) : null}
            {callState === 'elevated' ? (
              <Badge variant="warningSoft" className="font-normal shrink-0">
                Elevated
              </Badge>
            ) : null}
            <Badge
              variant={badgeVariantForStatus(row.call_status)}
              className="font-normal shrink-0"
            >
              {row.call_status || 'unknown'}
            </Badge>
            {row.call_type ? (
              <Badge variant="outline" className="font-normal shrink-0">
                {row.call_type}
              </Badge>
            ) : null}
          </div>
          <CallInitialsLandmark row={row} size="card" />
        </div>

        <div className="min-w-0">
          <div className="text-base font-semibold leading-tight truncate">
            {callerLabel}
          </div>
          {subtitle ? (
            <div className="text-sm text-muted-foreground mt-0.5 truncate">{subtitle}</div>
          ) : null}
          {row.assistant_name?.trim() ? (
            <div className="text-sm text-muted-foreground mt-0.5 truncate">
              {row.assistant_name.trim()}
            </div>
          ) : null}
        </div>

        {primaryBlock ? (
          <div className="rounded-md bg-muted/70 px-3 py-2 text-sm">
            <span className="font-medium text-foreground">{primaryBlock.label}: </span>
            <span className="text-muted-foreground line-clamp-4">{primaryBlock.text}</span>
          </div>
        ) : null}
        {triageSecondary ? (
          <div className="rounded-md border border-border/80 bg-muted/40 px-3 py-2 text-xs leading-snug">
            <span className="font-medium text-foreground">{triageSecondary.label}: </span>
            <span className="text-muted-foreground line-clamp-3">{triageSecondary.text}</span>
          </div>
        ) : null}

        {callState === 'emergency' || callState === 'elevated' ? (
          <p className="text-xs text-muted-foreground">
            AI-assessed urgency — verify before acting.
          </p>
        ) : null}

        {CALLS_TRIAGE_DEMO_UI_ENABLED && callState === 'emergency' && !row.is_complete ? (
          <div className="pointer-events-auto">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="w-full sm:w-auto shadow-sm"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toast.message('Demo only', {
                  description: 'Intercept / transfer is not connected yet.',
                })
              }}
            >
              Intercept / transfer call
            </Button>
          </div>
        ) : null}

        <div className="text-xs text-muted-foreground space-y-1">
          {inCompletionDwell ? null : live ? (
            <div>
              {elapsed ? (
                <>
                  On call <span className="text-foreground font-medium">{elapsed}</span>
                  {started && startedAtClock ? (
                    <>
                      {' '}
                      · started at {startedAtClock}
                    </>
                  ) : null}
                </>
              ) : createdFallback ? (
                <>Created {createdFallback}</>
              ) : (
                <>Not started</>
              )}
            </div>
          ) : (
            <div>
              {row.ended_at ? (
                <>
                  Ended {formatRelativeTimeIso(row.ended_at)} ·{' '}
                  <span className="text-foreground font-medium">
                    {formatDurationClock(row.duration_seconds)}
                  </span>
                </>
              ) : (
                <>Ended · {formatDurationClock(row.duration_seconds)}</>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-x-2 gap-y-1 items-baseline">
            <span className="font-medium text-foreground">Review:</span>
            {row.evaluation_status === 'not_started' && row.is_complete ? (
              <span>Not run yet</span>
            ) : row.evaluation_status === 'not_started' ? (
              <span>Pending</span>
            ) : row.evaluation_status === 'evaluating' ? (
              <span>In progress</span>
            ) : row.evaluation_status === 'completed' ? (
              <span>{stubEval ? 'Basic auto-check done' : 'Review complete'}</span>
            ) : (
              <span>{row.evaluation_status}</span>
            )}
          </div>
          {row.evaluation_status === 'completed' && stubEval && evalSummary ? (
            <details className="pointer-events-auto rounded border bg-muted/30 px-2 py-1">
              <summary className="cursor-pointer text-foreground font-medium">
                Auto-check details
              </summary>
              <p className="mt-1 text-muted-foreground leading-snug">{evalSummary}</p>
            </details>
          ) : null}

          {row.ended_reason ? (
            <div>Reason: {row.ended_reason}</div>
          ) : null}

          <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/50">
            <div className="font-mono text-xs text-muted-foreground/90 min-w-0 truncate">
              {row.vapi_call_id}
            </div>
            <span
              className={cn(
                buttonVariants({ variant: 'default', size: 'sm' }),
                'pointer-events-none shrink-0 shadow-sm'
              )}
            >
              View call
            </span>
          </div>
        </div>

        {completionDwell ? (
          <div className="rounded-md border border-warning/50 bg-warning/10 dark:bg-warning/15 px-3 py-2 space-y-2 mt-2">
            <p className="text-xs font-medium text-warning">
              Call ended — moving to Completed in {completionDwell.secondsLeft}s
            </p>
            <Progress value={completionDwell.progress} />
          </div>
        ) : null}
      </div>
    </div>
  )
}
